import { Button } from '@mui/joy';

import { Dispatch, SetStateAction, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { Skeleton } from '@/components/Skeleton';
import useToast from '@/components/Toast/useToast';
import {
  useCreateCustomChartMutation,
  useDashboards,
  useOrganizationId,
  useSingleChartData,
} from '@/hooks/useSwr';
import { appDashboardsIndexPath, appOrganizationPath } from '@/utils/constants';

import { DashboardSelector } from './CustomChartCrudPaneComponents';

export function CloneChartDialog({
  chartToCloneId,
  setChartToCloneId,
  mutateChartData,
}: {
  chartToCloneId: string | null;
  setChartToCloneId: Dispatch<SetStateAction<string | null>>;
  mutateChartData?: () => void;
}) {
  const [destinationDashboardId, setDestinationDashboardId] = useState<
    string | null
  >(null);
  const { createToast } = useToast();
  const organizationId = useOrganizationId();
  const { dashboardId: currentDashboardId } = useParams();

  const { data: dashboards, isLoading: dashboardsLoading } = useDashboards(
    destinationDashboardId
      ? { ids: [destinationDashboardId], limit: 1, offset: 0 }
      : null
  );
  const { data: singleChartData, isLoading: isLoadingSeries } =
    useSingleChartData(
      {
        omit_data: true,
      },
      chartToCloneId ? chartToCloneId : undefined,
      {
        revalidateOnFocus: false,
      }
    );

  const dashboard = dashboards?.[0];

  const {
    trigger: createTrigger,
    error: createError,
    isMutating: isMutatingCreate,
  } = useCreateCustomChartMutation({
    onSuccess: () => {
      if (currentDashboardId === destinationDashboardId) {
        mutateChartData?.();
      }
      setChartToCloneId(null);
      setDestinationDashboardId(null);
      createToast({
        title: 'Chart cloned',
        description: (
          <div>
            Your chart has been cloned successfully to{' '}
            <Link
              to={`/${appOrganizationPath}/${organizationId}/${appDashboardsIndexPath}/${destinationDashboardId}`}
              className="text-[var(--brand-400)] underline"
              replace
              reloadDocument
            >
              {dashboard?.title}
            </Link>
          </div>
        ),
        error: false,
      });
    },
  });

  const handleClone = () => {
    if (!destinationDashboardId || !singleChartData) {
      return;
    }

    // need to clone the raw data from pg instead of
    // using data returned to render the chart.
    createTrigger({
      json: {
        ...singleChartData,
        section_id: destinationDashboardId,
        title: `Copy of ${singleChartData.title}`,
      },
    });
  };

  return (
    <Dialog
      open={!!chartToCloneId}
      onOpenChange={(open) => {
        if (!open) setChartToCloneId(null);
      }}
    >
      <DialogContent
        title={
          isLoadingSeries ? (
            <Skeleton className="m-0 h-7 w-[300px]" />
          ) : (
            `Clone ${singleChartData?.title}`
          )
        }
      >
        <DashboardSelector
          onChange={setDestinationDashboardId}
          value={destinationDashboardId}
        />
        {createError && (
          <div className="text-error">
            Failed to clone dashboard: {createError.message}
          </div>
        )}
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleClone}
            disabled={dashboardsLoading || isLoadingSeries}
            loading={isMutatingCreate}
          >
            Clone
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
